import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { getProducts, getCategories } from '../services/api';
import { preloadProductImages } from '../utils/ImagePreloader';

// Criar o contexto
const ProductContext = createContext();

// Hook personalizado para usar o contexto
export function useProducts() {
  return useContext(ProductContext);
}

// Provedor do contexto
export function ProductProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [sortBy, setSortBy] = useState('created_at'); // Padrão: mais recentes
  const [sortOrder, setSortOrder] = useState('DESC');   // Padrão: descendente

  // Cache para armazenar produtos pré-carregados
  const [preloadedProducts, setPreloadedProducts] = useState({});
  
  // Ref para controlar chamadas em andamento e evitar duplicações
  const pendingRequests = useRef({});
  
  // Carregar produtos do backend - otimizado para evitar chamadas múltiplas
  const loadProducts = useCallback(async (page = pagination.page, limit = pagination.limit, category = null, search = null, silent = false) => {
    try {
      // Gerar uma chave única para esta consulta
      const queryKey = `${page}_${limit}_${category || 'all'}_${search || 'none'}_${sortBy}_${sortOrder}`;
      
      // Verificar se já temos estes produtos em cache
      if (preloadedProducts[queryKey]) {
        console.log('Usando produtos em cache para:', { page, limit, category, search });
        if (!silent) {
          setProducts(preloadedProducts[queryKey].products);
          setPagination(preloadedProducts[queryKey].pagination);
          setError(null);
          setLoading(false); // Garantir que loading seja false quando usar cache
        }
        
        // Pré-carregar imagens mesmo quando usando cache
        if (preloadedProducts[queryKey].products && preloadedProducts[queryKey].products.length > 0) {
          preloadProductImages(preloadedProducts[queryKey].products);
        }
        
        return preloadedProducts[queryKey];
      }
      
      // Verificar se já existe uma chamada em andamento para esta mesma consulta
      if (pendingRequests.current[queryKey]) {
        console.log('Chamada já em andamento para:', { page, limit, category, search });
        return pendingRequests.current[queryKey]; // Retornar a promise existente
      }
      
      console.log('Carregando produtos:', { page, limit, category, search, silent });
      
      // Sempre definir loading como true quando iniciar uma nova busca (exceto se for silencioso)
      if (!silent) {
        setLoading(true);
      }
      
      // Limpar produtos anteriores para evitar que produtos antigos sejam exibidos durante a busca
      if (search && !silent) {
        setProducts([]);
        // Para termos de busca comuns, garantir que o limite seja alto o suficiente
        const commonTerms = ['kit', 'creme', 'perfume', 'shampoo', 'batom', 'esmalte'];
        if (commonTerms.includes(search.trim().toLowerCase()) && limit < 1000) {
          limit = 1000; // Aumentar o limite para termos comuns
          console.log(`Termo comum '${search}' detectado, aumentando limite para ${limit}`);
        }
      }
      
      // Registrar esta chamada como pendente
      const requestPromise = getProducts(page, limit, category, search, sortBy, sortOrder);
      pendingRequests.current[queryKey] = requestPromise;
      
      // Buscar produtos do backend
      const response = await requestPromise;
      
      // Remover do registro de chamadas pendentes
      delete pendingRequests.current[queryKey];
      
      console.log('Produtos carregados:', response.products.length);
      if (search) {
        console.log('Termo de busca:', search);
        console.log('Resultados da busca:', response.products.map(p => p.name).join(', ').substring(0, 100) + '...');
      }
      
      // Pré-carregar imagens dos produtos carregados
      if (response.products && response.products.length > 0) {
        preloadProductImages(response.products);
      }
      
      // Armazenar no cache para uso futuro
      setPreloadedProducts(prev => {
        // Limitar o tamanho do cache para evitar uso excessivo de memória
        const entries = Object.entries(prev);
        if (entries.length > 20) {
          // Manter apenas as 10 entradas mais recentes
          const newCache = {};
          entries.slice(-10).forEach(([key, value]) => {
            newCache[key] = value;
          });
          return {
            ...newCache,
            [queryKey]: {
              products: response.products,
              pagination: response.pagination
            }
          };
        }
        
        return {
          ...prev,
          [queryKey]: {
            products: response.products,
            pagination: response.pagination
          }
        };
      });
      
      // Se for uma requisição silenciosa, não atualizar o estado
      if (silent) {
        return response;
      }
      
      // Verificar se temos resultados da busca
      if (search && response.products.length === 0) {
        console.log('Nenhum produto encontrado para o termo:', search);
        setError(`Nenhum produto encontrado para "${search}". Tente outro termo de busca.`);
      } else {
        setError(null);
      }
      
      setProducts(response.products);
      setPagination(response.pagination);
      
      return response;
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
      setError('Não foi possível carregar os produtos. Por favor, tente novamente mais tarde.');
      // Definir produtos como array vazio para evitar erros
      setProducts([]);
      setPagination({ page, limit, total: 0, totalPages: 0 });
      throw err; // Propagar o erro para quem chamou a função
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [sortBy, sortOrder, pagination.page, pagination.limit, preloadedProducts]);

  // Carregar categorias do backend
  const loadCategories = async () => {
    // Se já temos categorias, não precisamos carregar novamente
    if (categories.length > 0) {
      console.log('Categorias já carregadas, ignorando requisição');
      return;
    }
    
    try {
      console.log('Carregando categorias...');
      
      // Buscar categorias do backend
      const response = await getCategories();
      
      console.log('Categorias carregadas do backend:', response);
      setCategories(response);
    } catch (err) {
      console.error('Erro ao carregar categorias:', err);
      // Em caso de erro, definir um array vazio para evitar erros na UI
      setCategories([]);
    }
  };

  // Carregar dados iniciais de forma mais simples
  useEffect(() => {
    const initializeData = () => {
      // Evitar recarregar se já tiver produtos ou se já estiver carregando
      if (products.length === 0 && !loading) {
        // Usar os valores de sortBy e sortOrder do estado, que agora são 'created_at' e 'DESC'
        loadProducts(1, pagination.limit); 
      }
      // Carregar categorias independentemente se ainda não foram carregadas
      if (categories.length === 0) {
        loadCategories();
      }
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Executar apenas uma vez na montagem

  // Valores a serem disponibilizados pelo contexto
  const value = {
    products,
    categories,
    loading,
    error,
    pagination,
    loadProducts,
    loadCategories,
    sortBy,
    sortOrder,
    setSortCriteria: (newSortBy, newSortOrder) => {
      setSortBy(newSortBy);
      setSortOrder(newSortOrder);
      // Chamar loadProducts aqui pode ser uma opção, mas é melhor que o componente que usa o contexto
      // observe mudanças em sortBy/sortOrder e chame loadProducts. 
      // No entanto, para garantir que a mudança dispare o recarregamento, vamos chamar explicitamente.
      // Resetar para a página 1 ao mudar a ordenação.
      setPagination(prev => ({ ...prev, page: 1 })); 
      // loadProducts será chamado pelo useEffect no HomePage devido à mudança em sortBy/sortOrder se estiverem nas dependências,
      // ou podemos chamar diretamente. Para maior clareza e controle imediato:
      // loadProducts(1, pagination.limit, null, null); // Passando null para category e search
      // A chamada a loadProducts será feita pelo useEffect que observa sortBy e sortOrder no HomePage
    }
  };

  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
}

export default ProductProvider;
